import { useMemo, useState } from "react";

import { usePostHog } from "posthog-js/react";
import { useSWRConfig } from "swr";

import { ClientAppEvent } from "constants/index";
import { useSentFollowRequests, useWriterComponentState } from "hooks";
import {
  extractAPIRequestErrorMessage,
  revalidateSentFollowRequestCaches,
} from "utils";

import { AddCarrierToNetowrkButton } from "./AddCarrierToNetworkButton";
import { AddCarrierToNetworkLink } from "./AddCarrierToNetworkLink";
import { AddPartnerOutcomeSnackbar } from "./AddPartnerOutcomeSnackbar";
import {
  ADD_PARTNER_ERROR_FALLBACK,
  ADD_PARTNER_SUCCESS_MESSAGE,
} from "./constants";

import type { AxiosError } from "axios";

import type { FollowShipperRequestSource } from "constants/index";

import type { AddCarrierAnnouncement } from "./value_objects";

interface Props {
  carrierId: number;
  source: FollowShipperRequestSource;
  buttonVariant?: "contained" | "outlined";
  checkForRequestAlreadySent?: boolean;
  sentRequests?: FollowShipperRequestAndResponse[] | null;
  // Lets a caller that already watched this carrier get an invite report it as
  // sent. An add that auto-accepts answers its own request, so it never shows up
  // in the sent list this component reads.
  inviteAlreadySent?: boolean;
  display?: "button" | "link";
  // Renders the partner state in place of the action. Link presentation only.
  isPartner?: boolean;
  // Extra context for the analytics event, e.g. the quote and shipment the
  // affordance was rendered on.
  analyticsProperties?: Record<string, number | string>;
  // Lets a caller announce the outcome itself. Worth doing wherever a successful
  // add removes this component from the page: an announcement it renders would
  // be unmounted along with it.
  onOutcome?: (announcement: AddCarrierAnnouncement) => void;
  // Fires as the request leaves, before any outcome. A caller rendering the
  // action once per quote can use it to settle this carrier's other rows, which
  // would otherwise still offer an add that is already on its way.
  onSubmitStarted?: VoidFunction;
}

interface AddCarrierToNetworkResponse {
  code?: "AUTO_ACCEPT_FAILED";
}

// The two auto-accept outcomes are deliberately indistinguishable in the UI (see
// ADD_PARTNER_COPY in ./constants); the distinction survives only here, because
// how often adds land in each outcome is worth measuring.
type AddCarrierOutcome =
  "AUTO_ACCEPT_SUCCESS" | "AUTO_ACCEPT_FAILED" | "FAILED";

export function AddCarrierToNetworkButtonContainer(props: Props) {
  const {
    carrierId,
    source,
    buttonVariant = "outlined",
    checkForRequestAlreadySent = true,
    sentRequests = null,
    inviteAlreadySent = false,
    display = "button",
    isPartner = false,
    analyticsProperties,
    onOutcome,
    onSubmitStarted,
  } = props;
  const { mutate: revalidateAll } = useSWRConfig();
  const posthog = usePostHog();

  // A caller that already holds the list (one fetch shared across many rows)
  // passes it in, so this instance stays off the endpoint.
  const { sentFollowRequests: fetchedRequests } = useSentFollowRequests({
    enabled: checkForRequestAlreadySent && sentRequests == null,
  });

  const followShipperRequests = sentRequests ?? fetchedRequests ?? null;

  const requestAlreadySentForCarrier = useMemo(() => {
    if (followShipperRequests == null) return null;
    // The endpoint returns only requests still awaiting a response, so matching
    // the carrier is enough. target_role is null on external email invites.
    return followShipperRequests.some(
      (request) => request.request.target_role?.id === carrierId,
    );
  }, [followShipperRequests, carrierId]);

  const captureAddCarrierClicked = (
    outcome: AddCarrierOutcome,
    errorMessage?: string,
  ) => {
    posthog?.capture(ClientAppEvent.ADD_CARRIER_TO_NETWORK_CLICKED, {
      // Spread first so a caller's extra context cannot overwrite the fields
      // that identify the event.
      ...analyticsProperties,
      source,
      carrier_id: carrierId,
      outcome,
      ...(errorMessage != null && { error_message: errorMessage }),
    });
  };

  // Announcing the outcome and marking the affordance are separate concerns: the
  // announcement is transient, but a failed row must still say the add did not
  // happen once the toast is gone. `error` outlives this and is cleared by the
  // next write. Held locally only when the caller does not announce for us.
  const [announcement, setAnnouncement] =
    useState<AddCarrierAnnouncement | null>(null);
  const [attemptCount, setAttemptCount] = useState<number>(0);

  const announce = (
    severity: AddCarrierAnnouncement["severity"],
    message: string,
  ) => {
    const outcome = { severity, message, id: attemptCount };
    if (onOutcome) {
      onOutcome(outcome);
      return;
    }
    setAnnouncement(outcome);
  };

  const {
    loading: isSubmitting,
    error,
    success: inviteJustSent,
    write,
  } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: "/follow_requests/add_carrier",
      body: { carrier_id: carrierId, source },
    },
    errorMessage: ADD_PARTNER_ERROR_FALLBACK,
    onWriteFailure: (e) => {
      // The shipper is told the add did not happen, in our own words: the
      // rejections here are written for developers ("User already follows
      // shipper <email>"). The server's wording goes to analytics instead, where
      // it is what makes a spike diagnosable.
      announce("error", ADD_PARTNER_ERROR_FALLBACK);
      captureAddCarrierClicked(
        "FAILED",
        extractAPIRequestErrorMessage(
          e as AxiosError,
          ADD_PARTNER_ERROR_FALLBACK,
        ),
      );
    },
  });

  const onSubmit = () => {
    setAnnouncement(null);
    setAttemptCount((count) => count + 1);
    if (onSubmitStarted) {
      onSubmitStarted();
    }
    write<AddCarrierToNetworkResponse>((responseData) => {
      announce("success", ADD_PARTNER_SUCCESS_MESSAGE);
      captureAddCarrierClicked(
        responseData?.code === "AUTO_ACCEPT_FAILED"
          ? "AUTO_ACCEPT_FAILED"
          : "AUTO_ACCEPT_SUCCESS",
      );
      // Nothing else is refetched. An add settles the affordance where it
      // stands, and refreshing the shipment would rebuild the page the shipper
      // is working in — which also unmounts whatever is announcing the outcome
      // before it can be read.
      revalidateSentFollowRequestCaches(revalidateAll);
    });
  };

  const inviteSent =
    inviteAlreadySent ||
    inviteJustSent ||
    requestAlreadySentForCarrier === true;
  // Both presentations render their own waiting state, so the unknown
  // pending-invite state rides in on the same flag as the in-flight submit.
  const isLoading =
    isSubmitting ||
    (requestAlreadySentForCarrier == null && checkForRequestAlreadySent);

  return (
    <>
      {display === "link" ? (
        <AddCarrierToNetworkLink
          onSubmit={onSubmit}
          inviteSent={inviteSent}
          hasFailed={error != null}
          isLoading={isLoading}
          isPartner={isPartner}
        />
      ) : (
        <AddCarrierToNetowrkButton
          onSubmit={onSubmit}
          inviteSent={inviteSent}
          hasFailed={error != null}
          isLoading={isLoading}
          buttonVariant={buttonVariant}
        />
      )}
      {/* Dismissing this leaves the outcome on the affordance itself. */}
      <AddPartnerOutcomeSnackbar
        announcement={announcement}
        onClose={() => setAnnouncement(null)}
      />
    </>
  );
}
